import { useEffect, useMemo, useRef, useState } from 'react';
import { ChefHat, Package, Plus, Search } from 'lucide-react';

import type { StockItem } from '@/services/admin';
import { formatRub } from '@/utils/format';
import { formatQuantity } from '@/utils/units';

/**
 * Выбор ингредиента поиском вместо длинного `<select>`.
 *
 * Раньше при 60+ позициях склада приходилось листать выпадающий список вслепую,
 * а завести новую позицию можно было только выйдя из тех-карты — и потеряв
 * набранное. Теперь: печатаешь «мол» → «Молоко 3,2%», а если такого нет,
 * первым же пунктом предлагается создать позицию с этим названием.
 *
 * Заготовки показываются отдельной группой: у них своя себестоимость из
 * рецепта, и владельцу важно видеть, что он берёт не сырьё.
 */
export function IngredientPicker({
  items,
  value,
  excludeIds,
  autoFocus,
  onSelect,
  onCreate,
}: {
  items: StockItem[];
  value: string;
  /** Уже занятые в этой тех-карте позиции — второй раз их брать нельзя. */
  excludeIds?: Set<string>;
  autoFocus?: boolean;
  onSelect: (item: StockItem) => void;
  /** Создать позицию с введённым названием (кнопка появляется, если не найдено). */
  onCreate?: (name: string) => void;
}) {
  const selected = items.find((i) => i.id === value);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Клик вне — закрыть и вернуть отображение выбранного значения.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!boxRef.current?.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  useEffect(() => {
    if (autoFocus && !selected) {
      setOpen(true);
      inputRef.current?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const pool = items.filter((i) => i.id === value || !excludeIds?.has(i.id));
    const matched = q
      ? pool.filter(
        (i) => i.name.toLowerCase().includes(q) || (i.sku ?? '').toLowerCase().includes(q),
      )
      : pool;
    // Заготовки вперёд: их обычно немного, и именно их ищут осознанно.
    const preps = matched.filter((i) => i.kind === 'PREP');
    const goods = matched.filter((i) => i.kind !== 'PREP');
    return { preps, goods, total: matched.length };
  }, [items, query, value, excludeIds]);

  const flat = useMemo(() => [...filtered.preps, ...filtered.goods], [filtered]);
  const canCreate = !!onCreate && query.trim().length >= 2
    && !flat.some((i) => i.name.toLowerCase() === query.trim().toLowerCase());
  // Опция «создать» стоит последней в списке навигации стрелками.
  const optionCount = flat.length + (canCreate ? 1 : 0);

  const choose = (index: number) => {
    if (canCreate && index === flat.length) {
      onCreate?.(query.trim());
    } else {
      const item = flat[index];
      if (!item) return;
      onSelect(item);
    }
    setOpen(false);
    setQuery('');
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => (optionCount === 0 ? 0 : (h + 1) % optionCount));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => (optionCount === 0 ? 0 : (h - 1 + optionCount) % optionCount));
    } else if (e.key === 'Enter') {
      if (open && optionCount > 0) {
        e.preventDefault();
        e.stopPropagation();
        choose(highlight);
      }
    } else if (e.key === 'Escape') {
      if (open) {
        e.stopPropagation();
        setOpen(false);
        setQuery('');
      }
    }
  };

  return (
    <div className="relative" ref={boxRef}>
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <input
          ref={inputRef}
          className="input pl-8"
          placeholder="Начните вводить название…"
          value={open ? query : selected?.name ?? ''}
          onChange={(e) => {
            setQuery(e.target.value);
            setHighlight(0);
            setOpen(true);
          }}
          onFocus={() => { setOpen(true); setHighlight(0); }}
          onKeyDown={onKeyDown}
        />
        {selected?.kind === 'PREP' && !open && (
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-wide bg-violet-50 text-violet-700 border border-violet-200 rounded px-1.5 py-0.5">
            заготовка
          </span>
        )}
      </div>

      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-72 overflow-auto">
          {optionCount === 0 ? (
            <div className="px-3 py-3 text-sm text-slate-400">
              {items.length === 0 ? 'На складе пока нет позиций' : 'Ничего не найдено'}
            </div>
          ) : (
            <>
              {filtered.preps.length > 0 && <GroupLabel>Заготовки</GroupLabel>}
              {filtered.preps.map((item, i) => (
                <Option
                  key={item.id}
                  item={item}
                  active={highlight === i}
                  onHover={() => setHighlight(i)}
                  onClick={() => choose(i)}
                />
              ))}
              {filtered.goods.length > 0 && <GroupLabel>Товары</GroupLabel>}
              {filtered.goods.map((item, i) => {
                const index = filtered.preps.length + i;
                return (
                  <Option
                    key={item.id}
                    item={item}
                    active={highlight === index}
                    onHover={() => setHighlight(index)}
                    onClick={() => choose(index)}
                  />
                );
              })}
              {canCreate && (
                <button
                  type="button"
                  className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 border-t border-slate-100 ${
                    highlight === flat.length ? 'bg-indigo-50' : 'hover:bg-slate-50'
                  }`}
                  onMouseEnter={() => setHighlight(flat.length)}
                  onClick={() => choose(flat.length)}
                >
                  <Plus size={14} className="text-indigo-600" />
                  Создать позицию «{query.trim()}»
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function GroupLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wide text-slate-400 bg-slate-50/60">
      {children}
    </div>
  );
}

function Option({
  item, active, onHover, onClick,
}: {
  item: StockItem;
  active: boolean;
  onHover: () => void;
  onClick: () => void;
}) {
  const isPrep = item.kind === 'PREP';
  return (
    <button
      type="button"
      className={`w-full text-left px-3 py-2 flex items-center gap-2 ${active ? 'bg-indigo-50' : 'hover:bg-slate-50'}`}
      onMouseEnter={onHover}
      onClick={onClick}
    >
      {isPrep
        ? <ChefHat size={14} className="text-violet-600 shrink-0" />
        : <Package size={14} className="text-slate-400 shrink-0" />}
      <span className="flex-1 min-w-0 truncate text-sm">{item.name}</span>
      <span className="text-xs text-slate-400 tabular-nums shrink-0">
        {item.costPerUnit > 0 ? `${formatRub(item.costPerUnit)}/${item.unit}` : 'цена не задана'}
        {!isPrep && ` · ${formatQuantity(item.quantity, item.unit)}`}
      </span>
    </button>
  );
}
